import {
  CalendarPlus,
  Check,
  Link2,
  Pencil,
  Save,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { fromDatetimeLocal, toDatetimeLocal } from "../../lib/datetime";
import { apiFetch } from "../../lib/api";
import { pickCurrentEvent } from "@vzlive/shared";
import { useAdmin } from "./AdminShell";
import { StatusBadge } from "../../components/StatusBadge";
import { EventAlertPanels } from "../../components/admin/EventAlertPanels";
import type { AdminEvent } from "./types";

interface PluginResponse {
  installed: Array<{
    id: string;
    pluginId: string;
    eventId: string | null;
    enabled: boolean;
    config: Record<string, unknown>;
  }>;
}

export function AdminEventsPage() {
  const { data, refresh } = useAdmin();
  const [editingEvent, setEditingEvent] = useState<AdminEvent | null>(null);
  const [modalOpen, setModalOpen] = useState<"create" | "edit" | null>(null);
  const [plugins, setPlugins] = useState<PluginResponse | null>(null);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);

  const loadPlugins = () => {
    apiFetch<PluginResponse>("/api/admin/plugins").then(setPlugins);
  };
  useEffect(loadPlugins, []);

  const flash = (message: string) => {
    setSaved(message);
    window.setTimeout(() => setSaved(""), 2_200);
  };

  const currentEvent = pickCurrentEvent(data.events) ?? data.events[0] ?? null;

  const openModal = (target: "create" | AdminEvent) => {
    if (target === "create") {
      setEditingEvent(null);
      setModalOpen("create");
    } else {
      setEditingEvent(target);
      setModalOpen("edit");
    }
    dialogRef.current?.showModal();
  };

  const closeModal = () => {
    dialogRef.current?.close();
    setModalOpen(null);
    setEditingEvent(null);
  };

  const setEventStatus = async (eventId: string, status: string) => {
    setError("");
    try {
      await apiFetch(`/api/admin/events/${eventId}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      await refresh();
      flash(`Status geändert: ${status}`);
    } catch {
      setError("Eventstatus konnte nicht geändert werden.");
    }
  };

  const installedSetlist = (eventId: string) =>
    plugins?.installed.find(
      (plugin) =>
        plugin.pluginId === "setlist.u2tour" && plugin.eventId === eventId,
    );

  const saveSetlist = async (eventId: string, value: string) => {
    const enabled = value.trim().length > 0;
    await apiFetch("/api/admin/plugins/setlist.u2tour", {
      method: "PUT",
      body: JSON.stringify({
        eventId,
        enabled,
        config: enabled
          ? {
              showId: value,
              collection: "Show_Setlist",
              sortField: "position",
              songRelationField: "song",
            }
          : {},
      }),
    });
    loadPlugins();
  };

  const submitEvent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      title: form.get("title"),
      slug: form.get("slug"),
      description: form.get("description"),
      venue: form.get("venue") || undefined,
      startsAt: fromDatetimeLocal(String(form.get("startsAt") ?? "")),
      endsAt: fromDatetimeLocal(String(form.get("endsAt") ?? "")),
      showLiveStats: form.get("showLiveStats") === "on",
      showRelativeTime: form.get("showRelativeTime") === "on",
      chatEnabled: form.get("chatEnabled") === "on",
      timelineOrder: form.get("timelineOrder") || "newest",
    };
    setBusy("event");
    setError("");
    try {
      if (modalOpen === "edit" && editingEvent) {
        await apiFetch(`/api/admin/events/${editingEvent.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        const includeSetlist = form.get("setlistEnabled") === "on";
        if (includeSetlist && form.get("showId")) {
          await saveSetlist(editingEvent.id, String(form.get("showId")));
        } else if (
          !includeSetlist &&
          installedSetlist(editingEvent.id)?.enabled
        ) {
          await apiFetch("/api/admin/plugins/setlist.u2tour", {
            method: "PUT",
            body: JSON.stringify({ eventId: editingEvent.id, enabled: false }),
          });
          loadPlugins();
        }
        closeModal();
        await refresh();
        flash("Event aktualisiert");
      } else {
        await apiFetch("/api/admin/events", {
          method: "POST",
          body: JSON.stringify({
            ...payload,
            timezone: data.tenant.timezone,
            locale: data.tenant.locale,
          }),
        });
        closeModal();
        await refresh();
        flash("Event angelegt");
      }
    } catch {
      setError(
        "Das Event konnte nicht gespeichert werden. Prüfe die Eingaben.",
      );
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="admin-page events-page">
      <header className="admin-page-header">
        <div>
          <h1>Events</h1>
          <p>
            Lege Events an, verwalte Status und Ausgabe-Extras – alles direkt
            hier, ohne Einstellungen.
          </p>
        </div>
        {saved && (
          <span className="save-toast">
            <Check /> {saved}
          </span>
        )}
      </header>

      {!data.events.length && (
        <section className="admin-empty">
          <CalendarPlus />
          <h2>Noch kein Event</h2>
          <p>Lege dein erstes Event an, um loszulegen.</p>
          <button onClick={() => openModal("create")}>
            + Erstes Event anlegen
          </button>
        </section>
      )}

      {data.events.length > 0 && (
        <section className="settings-panel settings-panel--wide">
          <header>
            <h2>Event-Liste</h2>
            <span>{data.events.length}</span>
          </header>
          <div className="events-list">
            {data.events.map((event) => (
              <article key={event.id}>
                <StatusBadge
                  status={event.status}
                  locale={data.tenant.locale}
                />
                <div>
                  <strong>{event.title}</strong>
                  <small>
                    {event.venue || "Ohne Ort"} ·{" "}
                    {new Intl.DateTimeFormat("de", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(event.startsAt))}
                  </small>
                </div>
                <select
                  aria-label={`Status für ${event.title}`}
                  value={event.status}
                  onChange={(change) =>
                    void setEventStatus(event.id, change.target.value)
                  }
                >
                  <option value="scheduled">geplant</option>
                  <option value="hidden">versteckt</option>
                  <option value="live">live</option>
                  <option value="paused">pausiert</option>
                  <option value="archived">archiviert</option>
                  <option value="removed">entfernt</option>
                </select>
                <button
                  type="button"
                  className="icon-action"
                  aria-label={`${event.title} bearbeiten`}
                  onClick={() => openModal(event)}
                >
                  <Pencil size={17} />
                </button>
                <a
                  href={`/events/${event.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${event.title} im Reader öffnen`}
                >
                  <Link2 />
                </a>
              </article>
            ))}
          </div>
          <footer className="events-list__footer">
            <button onClick={() => openModal("create")}>
              <CalendarPlus size={17} /> Event anlegen
            </button>
          </footer>
        </section>
      )}

      {currentEvent && (
        <EventAlertPanels key={currentEvent.id} eventId={currentEvent.id} />
      )}

      <dialog
        ref={dialogRef}
        className="admin-dialog"
        aria-label={modalOpen === "edit" ? "Event bearbeiten" : "Event anlegen"}
      >
        <div className="admin-dialog__head">
          <h2>{modalOpen === "edit" ? "Event bearbeiten" : "Event anlegen"}</h2>
          <button
            type="button"
            className="admin-dialog__close"
            aria-label="Schließen"
            onClick={closeModal}
          >
            <X />
          </button>
        </div>
        <div className="admin-dialog__content">
          <form
            key={modalOpen === "edit" ? (editingEvent?.id ?? "edit") : "create"}
            onSubmit={submitEvent}
            className="settings-form"
          >
            <label>
              <span>Titel</span>
              <input
                name="title"
                required
                minLength={2}
                defaultValue={editingEvent?.title ?? ""}
              />
            </label>
            <label>
              <span>Slug</span>
              <input
                name="slug"
                required
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                placeholder="berlin-night-one"
                defaultValue={editingEvent?.slug ?? ""}
              />
            </label>
            <label className="wide">
              <span>Beschreibung</span>
              <textarea
                name="description"
                rows={3}
                defaultValue={editingEvent?.description ?? ""}
              />
            </label>
            <label className="wide">
              <span>Ort</span>
              <input name="venue" defaultValue={editingEvent?.venue ?? ""} />
            </label>
            <label>
              <span>Start</span>
              <input
                name="startsAt"
                type="datetime-local"
                required
                defaultValue={toDatetimeLocal(editingEvent?.startsAt)}
              />
            </label>
            <label>
              <span>
                Ende <small>optional</small>
              </span>
              <input
                name="endsAt"
                type="datetime-local"
                defaultValue={toDatetimeLocal(editingEvent?.endsAt)}
              />
            </label>
            <label className="switch-row wide">
              <span>Live-Stats im Reader</span>
              <input
                name="showLiveStats"
                type="checkbox"
                role="switch"
                defaultChecked={editingEvent?.showLiveStats ?? false}
              />
            </label>
            <label className="switch-row wide">
              <span>Relative Zeit anzeigen</span>
              <input
                name="showRelativeTime"
                type="checkbox"
                role="switch"
                defaultChecked={editingEvent?.showRelativeTime ?? false}
              />
            </label>
            <label className="switch-row wide">
              <span>Live-Chat für dieses Event</span>
              <input
                name="chatEnabled"
                type="checkbox"
                role="switch"
                defaultChecked={editingEvent?.chatEnabled ?? false}
              />
            </label>
            <label className="wide">
              <span>Timeline-Sortierung</span>
              <select
                name="timelineOrder"
                defaultValue={editingEvent?.timelineOrder ?? "newest"}
              >
                <option value="newest">Neueste zuerst</option>
                <option value="oldest">Älteste zuerst</option>
              </select>
            </label>
            {editingEvent && (
              <>
                <label className="switch-row wide">
                  <span>Setlist-Plugin anzeigen</span>
                  <input
                    name="setlistEnabled"
                    type="checkbox"
                    role="switch"
                    defaultChecked={installedSetlist(editingEvent.id)?.enabled}
                  />
                </label>
                <label className="wide">
                  <span>Directus Show-ID</span>
                  <input
                    name="showId"
                    placeholder="Show_Setlist-Show aus Directus"
                    defaultValue={String(
                      installedSetlist(editingEvent.id)?.config.showId ?? "",
                    )}
                  />
                </label>
              </>
            )}
            {!editingEvent && (
              <small className="admin-dialog__hint">
                Chat-Einstellungen wie Slow-Modus und Pause findest du im
                Live-Desk im Chat-Bereich.
              </small>
            )}
            {error && (
              <p className="admin-dialog__error" role="alert">
                {error}
              </p>
            )}
            <div className="settings-form-actions">
              <button type="submit" disabled={!!busy}>
                <Save size={17} />{" "}
                {modalOpen === "edit"
                  ? "Änderungen speichern"
                  : "Event anlegen"}
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={closeModal}
              >
                <XCircle size={17} /> Abbrechen
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </div>
  );
}
